import type { OpeningId } from "../lessons/ids.ts";

/**
 * How the Openings page groups courses. Neighbors share a position, or they
 * are the same sequence from the other side. `sharedMove` is the card step
 * (1, 2 or 3) where every board in the group is the same position. The note
 * says what the choice is and, when the boards match, which button to tap.
 * It quotes at most one measured club share. Claims are checked against the
 * guides; the longer comparison, with the other shares, is on each Theory page.
 */
export type CatalogFamily = {
  id: string;
  side: "white" | "black";
  /** What the group answers, as a small line above the title ("Against 1.e4"). */
  against: string;
  title: string;
  /** Two or three words for the jump list at the top of the page. */
  short: string;
  note: string;
  /** 1, 2 or 3 when every card in the group shows one position at that step. */
  sharedMove: 1 | 2 | 3 | null;
  openingIds: readonly OpeningId[];
};

export const CATALOG_FAMILIES: readonly CatalogFamily[] = [
  {
    id: "white-nf3",
    side: "white",
    against: "Against 1…e5",
    title: "One position, four plans for move 3",
    short: "1…e5, four plans",
    sharedMove: 2,
    note: "Tap 2. Nf3 on each card and the boards match. 1…e5 is 45.4% of club replies to 1.e4, so this is the position you will see most. Move 3 is the choice: Bc4 aims at f7, Bb5 attacks the knight that defends e5, d4 opens the center, and Nc3 develops first. Tap 4 for the answer to Black’s reply: after 3.Bc4, the Italian meets …Bc5 with 4.c3 and the Quiet Two Knights meets …Nf6 with 4.d3.",
    openingIds: ["italian", "quietTwoKnights", "ruyLopez", "scotch", "fourKnights"],
  },
  {
    id: "white-f4",
    side: "white",
    against: "Against 1…e5",
    title: "Offering the f-pawn",
    short: "f-pawn gambits",
    sharedMove: null,
    note: "Both give up the f-pawn for an open f-file and a fast attack. The King’s Gambit offers it at once, on move 2. The Vienna plays Nc3 first, so e4 is defended before the pawn moves. Tap 2. f4 and 3. f4 to compare.",
    openingIds: ["kingsGambit", "vienna"],
  },
  {
    id: "white-sicilian",
    side: "white",
    against: "Against 1…c5",
    title: "Two answers to the Sicilian",
    short: "Sicilian",
    sharedMove: null,
    note: "1…c5 is 17.6% of club replies to 1.e4. The courses split on move 2. Nf3 and 3.d4 open the center: faster development against Black’s extra central pawn. c3 keeps a pawn center and recaptures on d4 with a pawn.",
    openingIds: ["openSicilian", "alapin"],
  },
  {
    id: "white-pirc",
    side: "white",
    against: "Against 1…d6 and 1…g6",
    title: "A full center against the fianchetto",
    short: "Pirc and Modern",
    sharedMove: null,
    note: "Black leaves e5 and c5 empty, so you put pawns on both d4 and e4. Tap 3. Nc3: the knight holds e4 while the bishop comes to g7, and Black plans to castle before breaking with …e5.",
    openingIds: ["pircModern"],
  },
  {
    id: "white-e4-other",
    side: "white",
    against: "Against 1…e6, 1…d5 and 1…c6",
    title: "The French, the Scandinavian and the Caro-Kann",
    short: "French, Scandinavian, Caro-Kann",
    sharedMove: null,
    note: "Three ways Black strikes at e4 with the d-pawn. The French prepares it with …e6 and shuts in a bishop. The Caro-Kann prepares it with …c6 and keeps that bishop free. The Scandinavian strikes at once and brings the queen out. You answer the two pawn chains with 3.e5 and the early strike with 2.exd5.",
    openingIds: ["frenchAdvance", "scandinavian", "caroAdvance"],
  },
  {
    id: "white-d4",
    side: "white",
    against: "Opening with 1.d4",
    title: "Then c4 or Bf4",
    short: "1.d4",
    sharedMove: 1,
    note: "Tap 1. d4 and the boards match. 1.d4 is the second most common first move in club games, after 1.e4. 2.c4 offers a pawn to fight for the center. 2.Bf4 develops the bishop first and keeps a setup that does not depend on that offer.",
    openingIds: ["queensGambit", "london"],
  },
  {
    id: "white-flank",
    side: "white",
    against: "Opening with 1.c4 or 1.Nf3",
    title: "Ready to fianchetto",
    short: "English and Réti",
    sharedMove: null,
    note: "Tap 3. g3 on each card. Both put a bishop on g2 and let the center wait. The English starts with the c-pawn. The Réti starts with the knight and plays c4 second, which keeps a Queen’s Pawn opening in reserve.",
    openingIds: ["english", "reti"],
  },
  {
    id: "black-e5",
    side: "black",
    against: "Against 1.e4",
    title: "1…e5, then how to defend it",
    short: "1…e5",
    sharedMove: 1,
    note: "Tap 1… e5 and the boards match. 1…e5 is 45.4% of club replies to 1.e4. Move 2 is how you defend that pawn: …Nc6 develops and holds it, …Nf6 counterattacks e4 (Petroff), …d6 holds it with a pawn (Philidor). After …Nc6 the choice comes again on move 3, against the bishop on c4 or the bishop on b5, and each of those answers is its own course.",
    openingIds: [
      "openGame",
      "petroffDefense",
      "philidorDefense",
      "twoKnights",
      "berlinDefense",
      "steinitzDefense",
      "classicalRuy",
    ],
  },
  {
    id: "black-sicilian",
    side: "black",
    against: "Against 1.e4",
    title: "1…c5, then which Sicilian",
    short: "Sicilian",
    sharedMove: 1,
    note: "Tap 1… c5 and the boards match. 1…c5 is 17.6% of club replies to 1.e4: you fight for d4 from the flank, trade that pawn for White’s d-pawn, and use the c-file. Move 2 chooses the setup: …Nc6 develops toward d4, …d6 prepares the Najdorf’s …a6, and …e6 stays flexible and builds a hedgehog.",
    openingIds: ["sicilianNajdorf", "classicalSicilian", "kanSicilian"],
  },
  {
    id: "black-d5",
    side: "black",
    against: "Against 1.e4",
    title: "Striking at e4 with …d5",
    short: "French and Caro-Kann",
    sharedMove: null,
    note: "Tap 2… d5. Both challenge e4 with a prepared pawn, and the preparing move is the whole difference. …c6 keeps the light-squared bishop free. …e6 shuts it in, and the counterplay is …c5 against the base of White’s chain.",
    openingIds: ["caroKann", "frenchDefense"],
  },
  {
    id: "black-scandinavian",
    side: "black",
    against: "Against 1.e4",
    title: "1…d5 at once",
    short: "Scandinavian",
    sharedMove: null,
    note: "No preparation: you open the center on move 1. After 2.exd5 the queen recaptures, and 3…Qa5 steps aside so the other pieces can develop. The cost is a second queen move. The gain is an open board from the start.",
    openingIds: ["scandinavianDefense"],
  },
  {
    id: "black-pirc",
    side: "black",
    against: "Against 1.e4",
    title: "A fianchetto, then a break",
    short: "Pirc",
    sharedMove: null,
    note: "You let White build pawns on d4 and e4, then undermine them. Tap 2… Nf6: the knight attacks e4, …g6 and …Bg7 follow, and …e5 is the break once you have castled. 1…g6, the Modern, reaches the same setup and is a branch of this course.",
    openingIds: ["pircDefense"],
  },
  {
    id: "black-d4-d5",
    side: "black",
    against: "Against 1.d4",
    title: "1…d5, then the offered pawn",
    short: "1…d5",
    sharedMove: 1,
    note: "Tap 1… d5 and the boards match. Move 2 answers 2.c4, the offer of the c-pawn. …dxc4 accepts, and you will not keep the pawn: you develop and hit back with …c5. …e6 declines and supports d5, at the cost of the light-squared bishop. …c6 is the Slav: the same support with that bishop free.",
    openingIds: ["qgdBlack", "slavDefense", "qgaBlack"],
  },
  {
    id: "black-nf6",
    side: "black",
    against: "Against 1.d4",
    title: "1…Nf6, then two bishops",
    short: "1…Nf6",
    sharedMove: 1,
    note: "Tap 1… Nf6 and the boards match. No pawn in the center yet: the knight stops e4 and waits. Move 3 is the split. …Bb4 pins the knight on c3 and fights for e4 at once, often trading that bishop for doubled pawns. …Bg7 fianchettoes, castles, and only then breaks with …e5.",
    openingIds: ["nimzoIndian", "kingsIndian"],
  },
  {
    id: "black-c4",
    side: "black",
    against: "Against 1.c4",
    title: "Three answers to the English",
    short: "vs 1.c4",
    sharedMove: null,
    note: "They split on the first move, and the split is about the center. …e5 takes it at once, a Sicilian with the colors reversed. …c5 copies White and fights for d4 from the flank. …Nf6 develops, fianchettoes, and plays …e5 only after castling. Together the three are about 62% of club replies to 1.c4.",
    openingIds: ["kingsEnglish", "symmetricalEnglish", "englishNf6"],
  },
  {
    id: "black-reti",
    side: "black",
    against: "Against 1.Nf3",
    title: "Put a pawn in the center",
    short: "vs 1.Nf3",
    sharedMove: null,
    note: "1.Nf3 does not put a pawn in the center, so …d5 does. You meet c4 and the g2-bishop with …e6, …Nf6 and …Be7, castle, and then …c5 and …d4 claim the center White postponed. 1…Nf6 starts the same course as a fianchetto.",
    openingIds: ["retiBlack"],
  },
];
